import { useId, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, Search } from 'lucide-react';
import {
  Alert,
  IconTile,
  PageContainer,
  PageHeader,
  buttonClass,
  cardInteractive,
  cardSurface,
  cn,
} from '@castinflow/ui';
import { useSession } from '../lib/session';
import { usePageMeta } from '../shell/page-meta';
import { useShellSettings } from '../shell/shell-settings';
import {
  ADMIN_HUB_HREF,
  filterAreas,
  type VisibleCategory,
  type VisibleItem,
} from '../shell/navigation';
import { navText } from '../shell/navigation-strings';
import { matchPages, type NavPage } from '../shell/nav-context';

const T = navText.admin;

/** Ayar kartı (kategori sayfası ve arama sonuçları): ikon + ad + ne işe yaradığı; bütün kart tek bağlantıdır */
function SettingCard({ item, where }: { item: VisibleItem; where?: string }) {
  return (
    <li>
      <Link
        to={item.href ?? ADMIN_HUB_HREF}
        className={cn(
          cardSurface,
          cardInteractive,
          'group flex h-full items-start gap-3 px-4 py-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus',
        )}
        data-testid={`admin-card-${item.id}`}
      >
        <IconTile icon={item.icon} size="sm" />
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-1.5 text-base font-semibold text-fg">
            <span className="min-w-0 truncate">{item.label}</span>
            <ArrowRight
              size={15}
              aria-hidden="true"
              className="shrink-0 text-fg-subtle transition-transform duration-fast group-hover:text-primary motion-safe:group-hover:translate-x-0.5"
            />
          </span>
          {where ? <span className="block text-xs font-medium text-primary">{where}</span> : null}
          <span className="mt-0.5 block text-sm text-fg-muted">{T.items[item.id] ?? ''}</span>
        </span>
      </Link>
    </li>
  );
}

/** Kategori kartı (merkez): marka ikon kutusu, ad, açıklama, ayar sayısı ve ilk birkaç ayarın adı */
function CategoryCard({ cat }: { cat: VisibleCategory }) {
  return (
    <li>
      <Link
        to={cat.href}
        className={cn(
          cardSurface,
          cardInteractive,
          'group flex h-full flex-col gap-4 px-5 py-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus',
        )}
        aria-label={T.openCategory(cat.label)}
        data-testid={`admin-category-${cat.id}`}
      >
        <span className="flex items-start gap-4">
          <IconTile icon={cat.icon} emphasis="brand" />
          <span className="min-w-0 flex-1">
            <span className="block text-md font-semibold text-fg">{cat.label}</span>
            <span className="mt-0.5 block text-sm text-fg-muted">{cat.description}</span>
          </span>
        </span>
        <span className="mt-auto flex items-center justify-between gap-3 border-t border-border pt-3 text-sm">
          {/* Tema uyarlaması: kesilmez, gerekirse iki satıra iner (kesik ayar adı okunmuyordu; impeccable text-overflow) */}
          <span className="min-w-0 text-fg-subtle">
            {cat.items.length === 1 && cat.items[0]?.label === cat.label
              ? ''
              : cat.items
                  .slice(0, 3)
                  .map((i) => i.label)
                  .join(' · ') + (cat.items.length > 3 ? ' …' : '')}
          </span>
          <span className="inline-flex shrink-0 items-center gap-1 font-medium text-primary">
            {T.settingsCount(cat.items.length)}
            <ArrowRight
              size={15}
              aria-hidden="true"
              className="transition-transform duration-fast motion-safe:group-hover:translate-x-0.5"
            />
          </span>
        </span>
      </Link>
    </li>
  );
}

/**
 * v3.9 Yönetim merkezi (`/yonetim`) ve kategori sayfası (`/yonetim/:kategori`): uzun ayar listesi ana menüden kaldırıldı;
 * ayarlar beş kategoride, aranabilir. Görünen kategori ve ayarlar kullanıcının yetkisine göredir (boş kategori yok).
 * Bağlantılar mevcut ayar ekranlarına gider; rota ve yetki değişmez.
 */
export default function AdminHubPage() {
  const me = useSession((s) => s.me);
  const shell = useShellSettings();
  const { kategori } = useParams<{ kategori?: string }>();
  const nav = useMemo(
    () => filterAreas(me?.capabilities ?? [], shell.data),
    [me?.capabilities, shell.data],
  );
  const cat = kategori === undefined ? undefined : nav.admin.find((c) => c.id === kategori);
  const title = kategori === undefined ? T.hubTitle : (cat?.label ?? T.hubTitle);
  usePageMeta(title);
  const [q, setQ] = useState('');
  const searchId = useId();
  const pages = useMemo<NavPage[]>(
    () =>
      nav.admin.flatMap((c) =>
        c.items.map((i) => ({
          id: i.id,
          label: i.label,
          href: i.href ?? ADMIN_HUB_HREF,
          icon: i.icon,
          where: c.label,
          keywords: T.items[i.id] ?? '',
        })),
      ),
    [nav],
  );
  const results = q.trim() === '' ? null : matchPages(pages, q, 25);
  const itemById = (id: string) => nav.admin.flatMap((c) => c.items).find((i) => i.id === id);

  if (kategori !== undefined) {
    if (cat === undefined)
      return (
        <PageContainer>
          <PageHeader title={T.hubTitle} />
          <Alert tone="info" title={T.categoryNotFound}>
            <Link to={ADMIN_HUB_HREF} className={cn(buttonClass('secondary', 'sm'), 'mt-2')}>
              {T.hubBack}
            </Link>
          </Alert>
        </PageContainer>
      );
    return (
      <PageContainer>
        <PageHeader title={cat.label} description={cat.description} />
        <ul
          className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3"
          data-testid={`admin-category-page-${cat.id}`}
        >
          {cat.items.map((i) => (
            <SettingCard key={i.id} item={i} />
          ))}
        </ul>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader title={T.hubTitle} description={T.hubDescription(nav.admin.length)} />
      <div className="mb-6 max-w-xl">
        <label htmlFor={searchId} className="sr-only">
          {T.searchLabel}
        </label>
        <div className="relative">
          <Search
            size={18}
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-fg-subtle"
          />
          <input
            id={searchId}
            type="search"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
            }}
            placeholder={T.searchPlaceholder}
            autoComplete="off"
            data-testid="admin-search"
            className="h-11 w-full rounded-lg border border-border-strong bg-surface pl-10 pr-3 text-base text-fg shadow-card transition-[border-color,box-shadow] duration-fast placeholder:text-fg-subtle focus:border-primary focus:outline-none focus:ring-2 focus:ring-focus"
          />
        </div>
        <p className="sr-only" role="status" aria-live="polite">
          {results === null ? '' : T.searchResults(results.length)}
        </p>
      </div>
      {results !== null ? (
        results.length === 0 ? (
          <p className="text-base text-fg-muted" data-testid="admin-search-empty">
            {T.searchEmpty(q.trim())}
          </p>
        ) : (
          <ul
            className="grid grid-cols-1 gap-3 motion-safe:animate-fade-in sm:grid-cols-2 xl:grid-cols-3"
            data-testid="admin-search-results"
          >
            {results.map((p) => {
              const item = itemById(p.id);
              return item === undefined ? null : (
                <SettingCard key={p.id} item={item} where={p.where} />
              );
            })}
          </ul>
        )
      ) : (
        <ul
          className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3"
          data-testid="admin-hub"
        >
          {nav.admin.map((c) => (
            <CategoryCard key={c.id} cat={c} />
          ))}
        </ul>
      )}
    </PageContainer>
  );
}
